import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../config/config';
import { Sidebar } from '../moderator/Sidebar';
import { Navbar } from '../Navbar';

export default function EditIncome() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    itemName: "",
    personName: "",
    rate: "",
    qty: "",
    total: "0"
  });

  // Fetch Existing Data
  useEffect(() => {
    const fetchIncomeData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.baseUrl}/api/otherInc/getOthIncbyid/${id}`, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          }
        });
        
        if (!response.ok) throw new Error('Income record not found');

        const data = await response.json();
        // Backend normalization
        const item = Array.isArray(data) ? data[0] : (data.data ? data.data[0] : data);

        if (item) {
          setFormData({
            itemName: item.item_name || '',
            personName: item.person_name || '',
            rate: item.rate || '',
            qty: item.qty || '',
            total: item.total_amount || '0'
          });
        }
      } catch (error) {
        console.error('Error fetching income:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchIncomeData();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === "rate" || name === "qty") {
        const rate = name === "rate" ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
        const qty = name === "qty" ? parseFloat(value) || 0 : parseFloat(prev.qty) || 0;
        newData.total = (rate * qty).toString();
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/otherInc/editOthInc/${id}`, {
        method: 'PUT',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            item_name: formData.itemName,
            person_name: formData.personName,
            rate: formData.rate,
            qty: formData.qty,
            total_amount: formData.total
        })
      });

      if (!response.ok) throw new Error('Failed to update');
      navigate('/other-income/view'); 
    } catch (error) {
      console.error(error);
      alert('Failed to update income record');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) return (
    <div className="min-h-screen bg-gray-50"><Navbar /><Sidebar />
      <div className="ml-64 pt-24 p-8 flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        <p className="mt-4 text-gray-500 font-medium">Loading income data...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <Sidebar />
      
      <main className="ml-64 pt-24 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Update Other Income</h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                
                {/* Item Name */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Item Type</label>
                  <input
                    type="text"
                    name="itemName"
                    value={formData.itemName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm transition-all outline-none focus:border-green-500 focus:ring-2 focus:ring-green-50"
                    required
                  />
                </div>

                {/* Person Name */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Buyer / Person Name</label>
                  <input
                    type="text"
                    name="personName"
                    value={formData.personName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm transition-all outline-none focus:border-green-500 focus:ring-2 focus:ring-green-50"
                    required
                  />
                </div>

                {/* Rate */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Rate (Rs.)</label>
                  <div className="relative">

                    <input
                      type="number"
                      name="rate"
                      value={formData.rate}
                      onChange={handleInputChange}
                      className="w-full border border-gray-200 rounded-lg p-3 pl-10 text-sm transition-all outline-none focus:border-green-500 focus:ring-2 focus:ring-green-50"
                      required
                    />
                  </div>
                </div>

                {/* Quantity */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Quantity</label>
                  <input
                    type="number"
                    name="qty"
                    value={formData.qty}
                    onChange={handleInputChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm transition-all outline-none focus:border-green-500 focus:ring-2 focus:ring-green-50"
                    required
                  />
                </div>

                {/* Total Highlight */}
                <div className="md:col-span-2 bg-green-50 p-4 rounded-xl border border-green-100 flex justify-between items-center">
                    <span className="text-sm font-bold text-green-800">Updated Total Amount</span>
                    <span className="text-xl font-black text-green-700 font-mono">Rs. {formData.total}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2.5 rounded-lg text-sm font-bold text-gray-500 hover:bg-gray-100 transition-all"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-green-600 text-white px-10 py-2.5 rounded-lg text-sm font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-100 flex items-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}